import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IMenu, IStepProgress } from '@tecalisdev/infinity';
import { OPERATION_TYPE } from '../../operations/models/operations.models';
import { MenuItemAction } from '../../shared/models/master.models';
import { ModalConfirmationService } from '../../shared/services/modal-confirmation.service';
import {
	FormOcrOrderModalClose,
	FormOcrOrderModalMode,
} from '../components/modal-workflow-form-ocr-order/modal-workflow-form-ocr-order.component';
import { IOperationInfo, WorkflowStep } from '../models/workflows.models';
import {
	FORM_OCR_COINCIDENT_FIELD_IDS,
	hasCoincidentFieldWithMatchOcr,
	hasCoincidentFormOcrFields,
	isFormOcrOrderAutofillConfirmed as resolveFormOcrOrderAutofillConfirmed,
	isFormOcrOrderDocumentUi,
	shouldPromptFormOcrOrder,
	shouldShowFormOcrOrderSummary,
} from '../utils/form-ocr-autofill.util';
import {
	applyOperationsDisabledFromCompleted,
	areFormControlsValidExcept,
	isWorkflowOperationsSelectionInvalid,
	markPrecedingOperationsCompleted,
} from '../utils/workflow-form-control.util';
import { WorkflowFormValidationHost, WorkflowFormValidationService } from './workflow-form-validation.service';

/**
 * Host del stepper del editor (NIF-845).
 * El componente retiene DOM/scroll/locale focus y create/update HTTP.
 */
export interface WorkflowFormStepperHost {
	form: FormGroup;
	steps: Array<IStepProgress>;
	operations: IOperationInfo[];
	operationsFiltered: IOperationInfo[];
	showOperationsDetail: boolean;
	showError: string;
	id: string | undefined;
	showModalFormOcrOrder: { show: boolean; mode: FormOcrOrderModalMode };
	pendingOcrContinueIndex: number | null;
	readonly formStepsArray: FormArray;
	create(): void;
	update(): void;
	focusWorkflowLocaleOnInvalidStep(stepId: string): boolean;
	focusWorkflowLocaleForOperation(type: string): boolean;
	invalidateFormStepFieldTagsCache(): void;
	asValidationHost(): WorkflowFormValidationHost;
}

/**
 * Orquestación del stepper / menú de operaciones del editor (NIF-845).
 *
 * Abrir cuando: continue, next op, validación de selección, modal FORM/OCR, menú ops.
 * Scroll/DOM y create/update permanecen en el host.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormStepperService {
	private readonly validationService = inject(WorkflowFormValidationService);
	private readonly modalConfirmationService = inject(ModalConfirmationService);
	private readonly trans = inject(TranslateService);
	private readonly router = inject(Router);

	public onContinue(host: WorkflowFormStepperHost): void {
		const foundIndex = host.steps.findIndex((step: IStepProgress) => step.selected);
		if (foundIndex < 0) {
			return;
		}
		if (foundIndex === host.steps.length - 1) {
			if (!host.form.valid) {
				host.form.markAllAsTouched();
				this.scrollToFirstInvalidInTemplate();
				return;
			}
			host.id ? host.update() : host.create();
			return;
		}

		if (host.steps[1].selected && !this.checkOperationsSelectionValidity(host)) {
			return;
		}
		if (host.steps[foundIndex].id === WorkflowStep.OPERATIONS && !host.showOperationsDetail) {
			if (this.tryShowRequiredOcrModal(host)) {
				return;
			}
			host.showOperationsDetail = true;
			return;
		}
		if (host.form.get(host.steps[foundIndex].id).valid) {
			host.steps[foundIndex].selected = false;
			host.steps[foundIndex + 1].selected = true;
			return;
		}
		host.form.get(host.steps[foundIndex].id).markAllAsTouched();
		const localeScrollHandled = host.focusWorkflowLocaleOnInvalidStep(host.steps[foundIndex].id);
		if (!localeScrollHandled) {
			this.scrollToFirstInvalidInTemplate();
		}
	}

	public cancel(host: WorkflowFormStepperHost): void {
		if (host.form.get('general').valid) {
			this.modalConfirmationService.show({
				title: this.trans.instant('WORKFLOWS.FORM.MODAL_EXIT_TITLE'),
				description: this.trans.instant('WORKFLOWS.FORM.MODAL_EXIT_DESCRIPTION'),
				buttonPrimaryLabel: this.trans.instant('BUTTON.EXIT'),
				buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
				buttonPrimaryStyle: true,
			});
			return;
		}
		this.router.navigate(['workflows']);
	}

	public nextOperation(host: WorkflowFormStepperHost, index: number): void {
		const operationFilter = host.operationsFiltered.filter((opt, i) => opt.show && i > index);
		if (!operationFilter.length) {
			return;
		}
		const indexToChange = host.operationsFiltered.findIndex((opt) => opt.type === operationFilter[0].type);
		if (indexToChange < 0) {
			return;
		}
		host.operationsFiltered[indexToChange].selected = true;
		host.operationsFiltered[indexToChange].disabled = false;
		host.operationsFiltered[index].selected = false;
		host.form
			.get(`operations.${host.operationsFiltered[index].type}.completed`)
			.patchValue(true, { emitEvent: false });
		this.syncOperationsDisabledFromCompleted(host);
		setTimeout(() => {
			const collapse = document.getElementById(host.operationsFiltered[indexToChange].type);
			collapse?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		}, 200);
	}

	public globalContinue(host: WorkflowFormStepperHost): void {
		if (host.steps[1]?.selected && host.showOperationsDetail) {
			const i = host.operationsFiltered.findIndex((opt) => opt.selected);
			if (i === -1) {
				return;
			}
			if (!this.areOperationsValid(host, i)) {
				return;
			}
			const selectedOp = host.operationsFiltered[i];
			if (selectedOp?.type === OPERATION_TYPE.FORM && this.tryHandleFormOcrOrderPrompt(host, i)) {
				return;
			}
			if (i > -1 && i < host.operationsFiltered.length - 1) {
				this.nextOperation(host, i);
				return;
			}
			this.markPrecedingOperationsCompleted(host, i);
		}
		this.onContinue(host);
	}

	public tryHandleFormOcrOrderPrompt(host: WorkflowFormStepperHost, opIndex: number): boolean {
		const formAvailable = Boolean(host.form.get(`operations.${OPERATION_TYPE.FORM}.available`)?.value);
		const ocrAvailable = Boolean(host.form.get(`operations.${OPERATION_TYPE.OCR}.available`)?.value);
		const formSteps = host.formStepsArray.getRawValue() as Array<Record<string, unknown>>;
		const autofillControl = host.form.get(`operations.${OPERATION_TYPE.FORM}.autofill_from_ocr`);
		const autofillFromOcr = autofillControl?.value as boolean | null;

		if (
			formAvailable &&
			ocrAvailable &&
			hasCoincidentFormOcrFields(formSteps) &&
			hasCoincidentFieldWithMatchOcr(formSteps)
		) {
			autofillControl?.setValue(false);
			return false;
		}

		if (
			!shouldPromptFormOcrOrder({
				formAvailable,
				ocrAvailable,
				formSteps,
				autofillFromOcr,
			})
		) {
			return false;
		}

		host.pendingOcrContinueIndex = opIndex;
		host.showModalFormOcrOrder = { show: true, mode: 'continue' };
		return true;
	}

	public onFormOcrOrderModalClose(host: WorkflowFormStepperHost, event: FormOcrOrderModalClose): void {
		const mode = host.showModalFormOcrOrder.mode;
		host.showModalFormOcrOrder = { show: false, mode };

		if (event.action === 'dismiss') {
			host.pendingOcrContinueIndex = null;
			return;
		}

		if (event.action === 'editForm') {
			host.pendingOcrContinueIndex = null;
			this.selectAndScrollToFormOperation(host);
			return;
		}

		host.form.get(`operations.${OPERATION_TYPE.FORM}.autofill_from_ocr`)?.setValue(event.autofillFromOcr);

		if (event.autofillFromOcr) {
			this.clearMatchOcrOnCoincidentFormFields(host);
		}

		if (host.pendingOcrContinueIndex == null) {
			return;
		}

		const continueIndex = host.pendingOcrContinueIndex;
		host.pendingOcrContinueIndex = null;
		if (continueIndex < host.operationsFiltered.length - 1) {
			this.nextOperation(host, continueIndex);
			return;
		}
		this.markPrecedingOperationsCompleted(host, continueIndex);
		this.onContinue(host);
	}

	public openFormOcrOrderEdit(host: WorkflowFormStepperHost): void {
		host.showModalFormOcrOrder = { show: true, mode: 'edit' };
	}

	public shouldShowFormOcrOrderItem(host: WorkflowFormStepperHost): boolean {
		return shouldShowFormOcrOrderSummary({
			formAvailable: Boolean(host.form.get(`operations.${OPERATION_TYPE.FORM}.available`)?.value),
			ocrAvailable: Boolean(host.form.get(`operations.${OPERATION_TYPE.OCR}.available`)?.value),
			formSteps: host.formStepsArray.getRawValue() as Array<Record<string, unknown>>,
		});
	}

	public isFormOcrOrderAutofill(host: WorkflowFormStepperHost): boolean {
		return isFormOcrOrderDocumentUi(
			host.form.get(`operations.${OPERATION_TYPE.FORM}.autofill_from_ocr`)?.value as boolean | null,
		);
	}

	public isFormOcrOrderAutofillConfirmed(host: WorkflowFormStepperHost): boolean {
		return resolveFormOcrOrderAutofillConfirmed(
			host.form.get(`operations.${OPERATION_TYPE.FORM}.autofill_from_ocr`)?.value as boolean | null,
		);
	}

	public clearMatchOcrOnCoincidentFormFields(host: WorkflowFormStepperHost): void {
		const coincidentIds = new Set<string>(FORM_OCR_COINCIDENT_FIELD_IDS);
		for (const stepControl of host.formStepsArray.controls) {
			const fields = stepControl.get('fields') as FormArray | null;
			if (!fields) {
				continue;
			}
			for (const fieldControl of fields.controls) {
				const id = String(fieldControl.get('id')?.value ?? fieldControl.get('name')?.value ?? '');
				if (!coincidentIds.has(id)) {
					continue;
				}
				fieldControl.get('match_ocr')?.setValue(false);
				const validators = (fieldControl.get('validators')?.value as string[]) ?? [];
				fieldControl
					.get('validators')
					?.setValue(validators.filter((validator) => validator !== 'match_ocr'));
			}
		}
		host.invalidateFormStepFieldTagsCache();
	}

	public selectAndScrollToFormOperation(host: WorkflowFormStepperHost): void {
		const index = host.operationsFiltered.findIndex((opt) => opt.type === OPERATION_TYPE.FORM);
		if (index < 0) {
			return;
		}
		this.selectAndScrollOperation(host, index, OPERATION_TYPE.FORM);
	}

	public markPrecedingOperationsCompleted(host: WorkflowFormStepperHost, targetIndex: number): void {
		markPrecedingOperationsCompleted(host.form, host.operationsFiltered, targetIndex);
		this.syncOperationsDisabledFromCompleted(host);
	}

	public markAllVisibleOperationsCompleted(host: WorkflowFormStepperHost): void {
		host.operationsFiltered = host.operations.filter((opt) => opt.show);
		for (const opt of host.operationsFiltered) {
			host.form.get(`operations.${opt.type}.completed`)?.patchValue(true, { emitEvent: false });
		}
	}

	public syncOperationsDisabledFromCompleted(host: WorkflowFormStepperHost): void {
		applyOperationsDisabledFromCompleted(host.operations, (type) =>
			Boolean(host.form.get(`operations.${type}.completed`)?.value),
		);
		host.operationsFiltered = host.operations.filter((opt) => opt.show);
	}

	public areOperationsValid(host: WorkflowFormStepperHost, i: number): boolean {
		return this.validationService.areOperationsValid(host.asValidationHost(), i);
	}

	public selectAndScrollOperation(host: WorkflowFormStepperHost, index: number, type: string): void {
		host.showOperationsDetail = true;
		host.operationsFiltered.forEach((opt, idx) => {
			opt.selected = idx === index;
		});
		const formGroup = host.form.get(`operations.${type}`);
		if (formGroup) {
			formGroup.markAllAsTouched();
		}
		const localeScrollHandled = host.focusWorkflowLocaleForOperation(type);
		setTimeout(() => {
			if (localeScrollHandled) {
				return;
			}
			const container = document.getElementById(type);
			if (!container) {
				return;
			}
			let target: HTMLElement | null = null;
			if (formGroup) {
				const path = this.findFirstInvalidControlPath(formGroup);
				if (path?.length) {
					const last = path[path.length - 1];
					target = container.querySelector(`[formcontrolname="${last}"]`) as HTMLElement | null;
					if (!target) {
						target = container.querySelector(`[name="${last}"]`) as HTMLElement | null;
					}
				}
			}
			if (!target) {
				target = container.querySelector('[aria-invalid="true"]') as HTMLElement | null;
			}
			if (!target) {
				const firstInvalidDom = container.querySelector('.ng-invalid') as HTMLElement | null;
				target = firstInvalidDom || container;
			}
			target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
			try {
				target.focus();
			} catch {
				// ignore
			}
		}, 200);
	}

	public findFirstInvalidControlPath(control: AbstractControl): string[] | null {
		return this.validationService.findFirstInvalidControlPath(control);
	}

	public formIsValidExcept(form: Partial<FormGroup>, field: string): boolean {
		return areFormControlsValidExcept(form.controls as Record<string, { valid: boolean }>, field);
	}

	public operationMenuChange(host: WorkflowFormStepperHost, action: IMenu, op: IOperationInfo): void {
		const actions = {
			[MenuItemAction.EDIT]: () => {
				this.moveToOperation(host, op);
			},
			[MenuItemAction.DELETE]: () => {
				if (op.type === OPERATION_TYPE.HOME) {
					host.form.get(`operations.${OPERATION_TYPE.HOME}.available`).patchValue(false);
				}
				if (op.type === OPERATION_TYPE.OCR) {
					host.form.get(`operations.${OPERATION_TYPE.OCR}.available`).patchValue(false);
				}
				if (op.type === OPERATION_TYPE.POL) {
					host.form.get(`operations.${OPERATION_TYPE.POL}.available`).patchValue(false);
				}
				if (op.type === OPERATION_TYPE.DOCUMENTS) {
					host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.available`).patchValue(false);
				}
				if (op.type === OPERATION_TYPE.CONNECTIONS) {
					host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.available`).patchValue(false);
				}
			},
		};

		actions[action.id]?.() ?? null;
	}

	public isOperationsSelectionInvalid(host: WorkflowFormStepperHost): boolean {
		return isWorkflowOperationsSelectionInvalid(
			{
				ocr: Boolean(host.form.get(`operations.${OPERATION_TYPE.OCR}.available`)?.value),
				pol: Boolean(host.form.get(`operations.${OPERATION_TYPE.POL}.available`)?.value),
				form: Boolean(host.form.get(`operations.${OPERATION_TYPE.FORM}.available`)?.value),
				documents: Boolean(host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.available`)?.value),
				connections: Boolean(host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.available`)?.value),
				attorneyPowers: Boolean(host.form.get(`operations.${OPERATION_TYPE.ATTORNEY_POWERS}.available`)?.value),
			},
			Boolean(host.steps[1]?.selected),
		);
	}

	public checkOperationsSelectionValidity(host: WorkflowFormStepperHost): boolean {
		const invalid = this.isOperationsSelectionInvalid(host);
		if (invalid) {
			host.showError = this.trans.instant('WORKFLOWS.FORM.ERROR_NO_OPERATION_SELECTED');
		} else if (host.showError !== '') {
			host.showError = '';
		}
		return !invalid;
	}

	public moveToOperation(host: WorkflowFormStepperHost, operation: IOperationInfo): void {
		host.steps.map((step: IStepProgress) => (step.selected = false));
		const operationStep = host.steps.find((step: IStepProgress) => step.id === WorkflowStep.OPERATIONS);
		if (operationStep) {
			operationStep.selected = true;
		}
		host.operations.forEach((opt: IOperationInfo) => {
			opt.selected = opt.type === operation.type;
		});
		this.markAllVisibleOperationsCompleted(host);
		this.syncOperationsDisabledFromCompleted(host);
	}

	public changeOperationSelected(host: WorkflowFormStepperHost, operation: IOperationInfo): void {
		if (operation.selected || operation.disabled) {
			return;
		}
		const targetIndex = host.operationsFiltered.findIndex((op) => op.type === operation.type);
		if (targetIndex > 0) {
			this.markPrecedingOperationsCompleted(host, targetIndex);
		}
		host.operationsFiltered.forEach((op: IOperationInfo) => {
			op.selected = op.type === operation.type;
		});
	}

	private tryShowRequiredOcrModal(host: WorkflowFormStepperHost): boolean {
		const hasAttorneyPowersSelected = host.operationsFiltered.find(
			(opt) => opt.type === OPERATION_TYPE.ATTORNEY_POWERS,
		);
		const hasConnectionsSelected = host.operationsFiltered.find(
			(opt) => opt.type === OPERATION_TYPE.CONNECTIONS,
		);
		const hasOcrSelected = host.operationsFiltered.some((opt) => opt.type === OPERATION_TYPE.OCR);
		if (!(hasAttorneyPowersSelected || hasConnectionsSelected) || hasOcrSelected) {
			return false;
		}
		this.modalConfirmationService.show({
			title: this.trans.instant('WORKFLOWS.FORM.MODAL_REQUIRED_OCR.TITLE'),
			description: this.trans.instant('WORKFLOWS.FORM.MODAL_REQUIRED_OCR.DESCRIPTION', {
				operations:
					(hasAttorneyPowersSelected
						? this.trans.instant('WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS')
						: '') +
					(hasAttorneyPowersSelected && hasConnectionsSelected ? ' y ' : '') +
					(hasConnectionsSelected
						? this.trans.instant('WORKFLOWS.FORM.OPERATIONS.CONNECTIONS')
						: ''),
			}),
			buttonPrimaryLabel: this.trans.instant('BUTTON.HAS_DATA'),
			buttonSecondaryLabel: this.trans.instant('BUTTON.ADD_IDENTITY'),
			id: 'required-ocr',
		});
		return true;
	}

	private scrollToFirstInvalidInTemplate(): void {
		const form = document.getElementsByClassName('template-create')[0];
		const firstInvalidControl = form?.getElementsByClassName('ng-invalid')[0];
		if (!firstInvalidControl) {
			return;
		}
		firstInvalidControl.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		(firstInvalidControl as HTMLElement).focus();
	}
}
